import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { UsersRepository } from '../users.repository'
import { UsersService } from '../users.service'
import { User, UserToken } from '../models/user.entity'
import { initPermissions, Permissions } from '../../policies'
import { Role, RolesService } from '../../roles'
import { initUser } from '../libs/user.lib'

interface UserDetailState {
  user: User
  error?: HttpErrorResponse
  permissions?: Permissions
  roles?: Role[]
  tenantId?: string
  isLoading: boolean
  isModified: boolean
  roleIds?: string[]
  isPermissionsModified: boolean
}

const USER_DETAIL_INITIAL_STATE: UserDetailState = {
  user: initUser(),
  isLoading: false,
  isModified: false,
  isPermissionsModified: false,
}

@Injectable()
export class UserDetailStore {
  constructor(
    private usersService: UsersService,
    private usersRepository: UsersRepository,
    private rolesService: RolesService,
  ) {}

  private state = signal<UserDetailState>(USER_DETAIL_INITIAL_STATE)

  // Selectors
  readonly user = computed(() => this.state().user)
  readonly permissions = computed(() => this.state().permissions)
  readonly roles = computed(() => this.state().roles)
  readonly tenantId = computed(() => this.state().tenantId)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly roleIds = computed(() => this.state().roleIds)
  readonly isErrored = computed(() => !!this.state().error)
  readonly isPermissionsModified = computed(
    () => this.state().isPermissionsModified,
  )

  // Effects

  init(user: User) {
    this.setData(user, false, [initPermissions(), undefined])
  }

  async load(userId?: string): Promise<void> {
    if (!userId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.usersService.read$(userId).subscribe({
        next: async (data) => {
          const extData = await Promise.all([
            this.getPermissions(data._id),
            this.getRoles(),
          ])
          resolve()
          this.setData(data, userId ? userId !== data._id : false, extData)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async save(user: User, notify?: boolean): Promise<boolean> {
    let updatedUser = user
    this.unsetError()

    if (this.roleIds() !== undefined) {
      const rolesUpdated = await this.upsertRoles(user)

      if (rolesUpdated === false) {
        return false
      }

      const stateUser = this.user()
      if (stateUser) {
        updatedUser = stateUser
      }
    }

    const isPermissionsModified = this.isPermissionsModified()

    if (isPermissionsModified === true) {
      const permissionsUpdated = await this.updatePermissions(updatedUser)
      if (permissionsUpdated === false) {
        return false
      }
      const stateUser = this.user()
      if (stateUser) {
        updatedUser = stateUser
      }
    }
    return new Promise((resolve) => {
      this.usersService.upsert$(updatedUser).subscribe({
        next: (data) => {
          resolve(true)
          this.updateUser(data)
          if (notify === true) {
            this.usersRepository.notify({ code: 'USER_SAVED' })
          }
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async create(user: User, notify?: boolean): Promise<boolean> {
    let updatedUser = user
    this.unsetError()

    return new Promise((resolve) => {
      this.usersService.upsert$(updatedUser).subscribe({
        next: async (data) => {
          this.updateUser(data)
          updatedUser = data
          if (notify === true) {
            this.usersRepository.notify({ code: 'USER_SAVED' })
          }
          if (this.roleIds() !== undefined) {
            const rolesUpdated = await this.upsertRoles(updatedUser)

            if (rolesUpdated === false) {
              resolve(false)
            }

            const stateUser = this.user()
            if (stateUser) {
              updatedUser = stateUser
            }
          }

          const isPermissionsModified = this.isPermissionsModified()

          if (isPermissionsModified === true) {
            const permissionsUpdated = await this.updatePermissions(updatedUser)
            if (permissionsUpdated === false) {
              resolve(false)
            }
            const stateUser = this.user()
            if (stateUser) {
              updatedUser = stateUser
            }
          }
          resolve(true)
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async getPermissions(userId: string): Promise<Permissions | undefined> {
    return new Promise<Permissions | undefined>((resolve) => {
      this.usersService.getPermissions$(userId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async getRoles(): Promise<Role[] | undefined> {
    return new Promise<Role[] | undefined>((resolve) => {
      this.rolesService.list$(undefined, true).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async enable(): Promise<boolean> {
    const user = this.user()
    if (!user) {
      return false
    }

    this.unsetError()

    return new Promise((resolve) => {
      this.usersService.enable$(user._id).subscribe({
        next: (data) => {
          resolve(true)
          this.updateUser(data, false)
          this.usersRepository.notify({ code: 'USER_SAVED' })
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async disable(): Promise<boolean> {
    const user = this.user()
    if (!user) {
      return false
    }

    this.unsetError()

    return new Promise((resolve) => {
      this.usersService.disable$(user._id).subscribe({
        next: (data) => {
          resolve(true)
          this.updateUser(data, false)
          this.usersRepository.notify({ code: 'USER_SAVED' })
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async createToken(): Promise<string | undefined> {
    const user = this.user()
    if (!user?._id) {
      return
    }

    this.unsetError()

    return new Promise((resolve) => {
      this.usersService.createToken$(user._id).subscribe({
        next: (data) => {
          resolve(data.token)
          this.usersRepository.notify({ code: 'TOKEN_CREATED' })
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async deleteToken(token?: UserToken): Promise<boolean> {
    const user = this.user()
    if (!user?._id || !token) {
      return false
    }

    this.unsetError()

    return new Promise((resolve) => {
      this.usersService.deleteToken$(user._id, token._id).subscribe({
        next: () => {
          resolve(true)
          this.usersRepository.notify({ code: 'TOKEN_DELETED' })
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async updatePassword(psw: string): Promise<boolean> {
    const user = this.user()
    if (!user?._id) {
      return false
    }

    this.unsetError()

    return new Promise((resolve) => {
      this.usersService.setPassword$(user._id, psw).subscribe({
        next: () => {
          resolve(true)
          this.usersRepository.notify({ code: 'PASSWORD_UPDATED' })
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async updateRoles(roleIds: string[]) {
    let stateRoles = this.roles()
    if (!stateRoles) {
      const newRoles = await this.getRoles()
      this.setRoles(newRoles ?? [])
      stateRoles = newRoles
    }

    this.setRoleIds(roleIds)
  }

  async upsertRoles(user: User): Promise<boolean> {
    this.unsetError()
    const tenantId = this.tenantId()
    if (!tenantId) {
      this.usersRepository.notify({ code: 'TENANT_NOT_FOUND' })
      return false
    }

    const roleIds: string[] = this.roleIds() ?? []
    return new Promise((resolve) => {
      this.usersRepository.updateUserRoles$(user, tenantId, roleIds).subscribe({
        next: (value: { user: User; permissions: Permissions }) => {
          resolve(true)
          this.usersRepository.notify({ code: 'USERS_ROLES_UPDATED' })
          this.setUserWithPermissions(value.user, value.permissions)
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async updatePermissions(user: User): Promise<boolean> {
    this.unsetError()
    const tenantId = this.tenantId()
    const permissions = this.permissions()

    if (!tenantId) {
      this.usersRepository.notify({ code: 'TENANT_NOT_FOUND' })
      return false
    }
    if (!permissions) {
      return false
    }

    return new Promise((resolve) => {
      this.usersService.setPermissions$(user._id, permissions).subscribe({
        next: (value) => {
          resolve(true)
          this.usersRepository.notify({ code: 'USERS_PERMISSIONS_UPDATED' })
          this.setPermissions(value, false)
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  // Reducers

  setData(
    user: User,
    isModified?: boolean,
    extData?: [Permissions | undefined, Role[] | undefined],
  ) {
    this.state.set({
      ...this.state(),
      user,
      isModified: isModified ?? this.state().isModified,
      permissions: extData?.[0] ?? this.permissions(),
      roles: extData?.[1] ?? this.roles(),
      isLoading: false,
      error: undefined,
    })
  }

  updateUser(user: User, isModified?: boolean) {
    this.state.set({
      ...this.state(),
      user,
      isModified: isModified ?? this.state().isModified,
      isLoading: false,
      error: undefined,
    })
  }

  setPermissions(permissions: Permissions, isPermissionsModified?: boolean) {
    this.state.set({
      ...this.state(),
      permissions,
      isPermissionsModified: isPermissionsModified ?? true,
      isLoading: false,
      error: undefined,
    })
  }

  setRoles(roles: Role[]) {
    this.state.set({
      ...this.state(),
      roles,
      isLoading: false,
      error: undefined,
    })
  }

  setRoleIds(roleIds: string[]) {
    this.state.set({
      ...this.state(),
      roleIds,
      isLoading: false,
      error: undefined,
    })
  }

  setTenantId(tenantId: string) {
    this.state.set({ ...this.state(), isLoading: false, tenantId })
  }

  setUserWithPermissions(user: User, permissions: Permissions) {
    this.state.set({
      ...this.state(),
      user,
      permissions,
      roleIds: undefined,
      isPermissionsModified: false,
      isLoading: false,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
